/** Пользователи (§51). */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { AdminUserRow } from '@alterium/shared';
import { adminApi } from '../../api/admin';
import { DataTable, type Column } from '../../components/admin/DataTable';
import { formatDate } from '../../i18n/format';
import { useTranslation } from 'react-i18next';

const date = (value: string): string => formatDate(value);

export function AdminUsersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');

  const users = useQuery({
    queryKey: ['admin', 'users', page, query],
    queryFn: () => adminApi.users({ page, query }),
  });

  const columns: Array<Column<AdminUserRow>> = [
    { key: 'email', title: t('auth.field.email'), render: (row) => row.email },
    { key: 'name', title: t('auth.field.name'), render: (row) => row.name ?? '—' },
    {
      key: 'status',
      title: t('admin.job.status'),
      render: (row) => (
        <span className={row.status === 'blocked' ? 'text-error' : 'text-content'}>
          {row.status}
        </span>
      ),
    },
    {
      key: 'verified',
      title: t('auth.field.email'),
      render: (row) =>
        row.emailVerified ? (
          <span className="text-content-secondary">{t('admin.user.verified')}</span>
        ) : (
          <span className="text-warning">{t('admin.user.unverified')}</span>
        ),
    },
    { key: 'persona', title: t('nav.personaShort'), render: (row) => (row.hasPersona ? t('common.yesShort') : '—') },
    { key: 'available', title: t('nav.balance'), render: (row) => row.available, align: 'right' },
    { key: 'reserved', title: t('admin.user.reserved'), render: (row) => row.reserved, align: 'right' },
    { key: 'created', title: t('admin.user.registered'), render: (row) => date(row.createdAt) },
  ];

  return (
    <div className="flex flex-col gap-4">
      <input
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setPage(1);
        }}
        placeholder={t('admin.user.searchPlaceholder')}
        className="h-9 w-64 rounded-control border border-line bg-surface px-3 text-meta text-content placeholder:text-content-muted focus:border-accent focus:outline-none"
      />

      <DataTable
        columns={columns}
        rows={users.data?.items ?? []}
        rowKey={(row) => row.id}
        total={users.data?.total}
        page={page}
        totalPages={users.data?.totalPages}
        loading={users.isLoading}
        onPage={setPage}
        onRowClick={(row) => navigate(`/admin/users/${row.id}`)}
      />
    </div>
  );
}
